import type { ButtonAltProps } from '@/dsComponents/buttons/types';
import type { TableAltRows } from '@/dsComponents/TableAlt/types';
import type { ProfilePreviewAgency } from '@/modules/worker/types';
import type { IAddress } from '@/types/address';
import type { IInterimJob, JobExperience } from '@/types/job';
import type { UserRole } from '@/types/user';
import type { CV } from '@/types/worker';

export interface FormattedProfile {
	id: string;
	firstname: string;
	lastname: string;
	email: string;
	phone: string;
	birthDate: string;
	address: IAddress | null;
	nationality: string;
	birthPlace: string;
	birthCountry: string;
	nir: string;
	jobs: IInterimJob[];
	roles: UserRole[] | undefined;
	cv: CV | null;
	recruitmentCount: number;
	agencies: ProfilePreviewAgency[];
}

export interface ProfilPreviewModalAction {
	button: ButtonAltProps;
	onClick?: (currentProfile: FormattedProfile) => void;
}

export interface ProfilPreviewModalProps {
	open: boolean;
	profilIds: string[];
	startingProfileId: string;
	actions?: ProfilPreviewModalAction[];
}

export interface ProfilPreviewModalHeaderProps {
	currentProfileIndex: number;
	currentProfile: FormattedProfile | null;
	totalProfiles: number;
	profileName: string;
	displayAside?: boolean;
	actions?: ProfilPreviewModalAction[];
}

export interface ProfilPreviewQualificationsProps {
	profile: FormattedProfile | null;
}

export interface QualificationsTableRow extends TableAltRows {
	job: IInterimJob;
}

export interface ProfilPreviewFormProps {
	profile: FormattedProfile | null;
	address: string;
}

export interface ProfilPreviewWarningModalProps {
	open: boolean;
}

export interface ProfilPreviewModalContentProps {
	cvPath: string | null;
}

export interface ProfilPreviewFormItemProps {
	label: string;
	icon: string;
}

export interface ProfilPreviewAlertProps {
	recruitmentCount: number;
}

export interface LabeledOperation {
	label: string;
	promise: Promise<void>;
}

// --- Backend API DTOs for profile update ---

export interface AddressPayload {
	street: string;
	complement?: string;
	city: string;
	zip: string;
	department: string;
	region: string;
	countryCode: string;
	inseeCode: string;
	location?: [longitude: number, latitude: number];
}

export interface UpdatePersonalInfoPayload {
	firstname?: string;
	lastname?: string;
	email?: string;
	phone?: string;
	birthday?: string;
	birthPlace?: string;
	birthCountry?: string;
	nationalityCode?: string;
	address?: AddressPayload;
}

export interface SetNirPayload {
	nir: string;
	nirKey?: string;
}

export interface AddJobPayload {
	jobId: string;
	jobName: string;
	experience: JobExperience;
	jobCode?: string;
	yearsOfExperience?: number;
	isMain?: boolean;
}

// --- ProfilPreviewFollowingHeader ---

export enum FollowingFilterPanel {
	Root = 'root',
	Agence = 'agence',
	Categorie = 'categorie',
}

export enum FollowingCategoryType {
	Needs = 'needs',
	Missions = 'missions',
	Candidatures = 'candidatures',
	Modifications = 'modifications',
	Contact = 'contact',
	Notes = 'notes',
	Interviews = 'interviews',
}

export interface AgencyOption {
	id: string;
	labelKey?: string;
	publicName?: string;
}

export interface ProfilPreviewFollowingHeaderProps {
	agencies?: AgencyOption[];
	selectedAgencyIds?: string[];
	selectedCategoryIds?: FollowingCategoryType[];
}
